import {
	useGetWorkflowDbData,
	useGetWorkflowDbSchema,
	useDeleteRecord,
	useUpdateRecord,
	useUpdateWorkflowDbSchema,
	useWriteRecord,
} from "@/features/workflow-db";
import {
	addFieldAtPath,
	createEmptyRecordFromSchema,
	createEmptyWorkflowDbSchema,
	getFieldsAtPath,
	hasDuplicateFieldKey,
	normalizeWorkflowDbSchema,
	normalizeWorkflowDbTableData,
	removeFieldAtPath,
	updateFieldAtPath,
} from "@/features/workflow-db/workflow-db.schema";
import type {
	WorkflowDbArrayItemType,
	WorkflowDbFieldSchema,
	WorkflowDbFieldType,
	WorkflowDbRecord,
	WorkflowDbSchema,
	WorkflowDbTableData,
} from "@/features/workflow-db/workflow-db.types";
import { SelectInputField, TextInputField } from "@/shared/ui/composites";
import { Button, HStack, Icon, Modal, Spinner, VStack } from "@/shared/ui/primitives";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type WorkflowDatabaseModalProps = {
	isOpen: boolean;
	workflowId: string;
	onClose: () => void;
};

type FieldModalMode = "add" | "edit";

type BinaryChoiceControlProps = {
	label: string;
	value: boolean;
	onChange: (value: boolean) => void;
	trueLabel?: string;
	falseLabel?: string;
	className?: string;
};

const FIELD_TYPE_OPTIONS: { label: string; value: WorkflowDbFieldType }[] = [
	{ label: "String", value: "string" },
	{ label: "Number", value: "number" },
	{ label: "Boolean", value: "boolean" },
	{ label: "Object", value: "object" },
	{ label: "Array", value: "array" },
	{ label: "Enum", value: "enum" },
	{ label: "File", value: "file" },
];

const ARRAY_ITEM_TYPE_OPTIONS: { label: string; value: WorkflowDbArrayItemType }[] = [
	{ label: "String", value: "string" },
	{ label: "Number", value: "number" },
	{ label: "Boolean", value: "boolean" },
	{ label: "Enum", value: "enum" },
];

const PANEL_SURFACE_CLASS = "rounded-[16px] border border-outline";
const INSET_SURFACE_CLASS = "rounded-[12px] border border-outline bg-bg-regular/30";
const SECTION_SURFACE_CLASS = "rounded-[12px] border border-outline bg-bg-regular/30";
const INPUT_SURFACE_CLASS =
	"w-full rounded-[10px] border border-outline px-[12px] py-[13px] text-sm text-ct-strong outline-none placeholder:text-ct-regular bg-bg-weak/60";
const CHIP_BUTTON_CLASS =
	"px-[8px] py-[4px] rounded-[999px] border border-outline text-sm text-ct-regular hover:bg-bg-regular/60 transition-colors";

const BinaryChoiceControl = ({
	label,
	value,
	onChange,
	trueLabel,
	falseLabel,
	className = "w-[132px]",
}: BinaryChoiceControlProps) => {
	const { t } = useTranslation();
	const resolvedTrueLabel = trueLabel ?? t("workflow.db.yes");
	const resolvedFalseLabel = falseLabel ?? t("workflow.db.no");
	return (
	<VStack className={`${className} items-start justify-start gap-[6px] p-[2px]`}>
		<span className="text-sm text-ct-strong px-[6px]">{label}</span>
		<HStack className="w-full rounded-[10px] bg-bg-regular p-[4px] gap-[4px] justify-start">
			<Button
				className="h-[40px] flex-1"
				variant={!value ? "primary" : "default"}
				onClick={() => onChange(false)}
			>
				{resolvedFalseLabel}
			</Button>
			<Button
				className="h-[40px] flex-1"
				variant={value ? "primary" : "default"}
				onClick={() => onChange(true)}
			>
				{resolvedTrueLabel}
			</Button>
		</HStack>
	</VStack>
	);
};

const NESTED_FIELD_TYPE_OPTIONS = FIELD_TYPE_OPTIONS.filter((o) => o.value !== "object");

type ObjectSchemaEditorProps = {
	fields: WorkflowDbFieldSchema[];
	onChange: (fields: WorkflowDbFieldSchema[]) => void;
};

const ObjectSchemaEditor = ({ fields, onChange }: ObjectSchemaEditorProps) => {
	const { t } = useTranslation();
	const [keyInput, setKeyInput] = useState("");
	const [typeInput, setTypeInput] = useState<WorkflowDbFieldType>("string");
	const [requiredInput, setRequiredInput] = useState(false);
	const [arrItemType, setArrItemType] = useState<WorkflowDbArrayItemType>("string");
	const [enumInput, setEnumInput] = useState("");
	const [enumOpts, setEnumOpts] = useState<string[]>([]);
	const [error, setError] = useState("");

	const handleAddEnum = () => {
		const v = enumInput.trim();
		if (!v) return;
		if (!enumOpts.includes(v)) setEnumOpts((c) => [...c, v]);
		setEnumInput("");
	};

	const handleAdd = () => {
		const key = keyInput.trim();
		if (!key) {
			setError(t("workflow.db.fieldKeyRequired"));
			return;
		}
		if (fields.some((f) => f.key === key)) {
			setError(t("workflow.db.fieldKeyExists"));
			return;
		}
		if (typeInput === "enum" && enumOpts.length === 0) {
			setError(t("workflow.db.enumRequired"));
			return;
		}

		const next: WorkflowDbFieldSchema = { key, type: typeInput, required: requiredInput };
		if (typeInput === "enum") next.enumOptions = enumOpts;
		if (typeInput === "array") {
			next.arrayItemType = arrItemType;
			if (arrItemType === "enum") next.enumOptions = enumOpts;
		}

		onChange([...fields, next]);
		setKeyInput("");
		setTypeInput("string");
		setRequiredInput(false);
		setArrItemType("string");
		setEnumInput("");
		setEnumOpts([]);
		setError("");
	};

	const showEnumEditor =
		typeInput === "enum" || (typeInput === "array" && arrItemType === "enum");

	return (
		<VStack className="w-full items-stretch justify-start gap-[8px] border border-outline rounded-[10px] p-[10px]">
			<TextInputField
				label={t("workflow.db.nestedFieldKey")}
				value={keyInput}
				onChange={(e) => {
					setError("");
					setKeyInput(e.target.value);
				}}
				placeholder="ex) fieldName"
				clearable
				errorMessage={error || undefined}
			/>
			<HStack className="w-full items-end gap-[8px]">
				<div className="flex-1 min-w-0">
					<SelectInputField
						label={t("workflow.db.nestedType")}
						value={typeInput}
						onChange={(v: string) => setTypeInput(v as WorkflowDbFieldType)}
						options={NESTED_FIELD_TYPE_OPTIONS}
					/>
				</div>
				<BinaryChoiceControl label={t("workflow.db.required")} value={requiredInput} onChange={setRequiredInput} />
			</HStack>
			{typeInput === "array" ? (
				<SelectInputField
					label={t("workflow.db.arrayItemType")}
					value={arrItemType}
					onChange={(v: string) => setArrItemType(v as WorkflowDbArrayItemType)}
					options={ARRAY_ITEM_TYPE_OPTIONS}
				/>
			) : null}
			{showEnumEditor ? (
				<VStack className="w-full items-stretch justify-start gap-[6px] border border-outline rounded-[8px] p-[8px]">
					<HStack className="w-full gap-[6px]">
						<input
							className={INPUT_SURFACE_CLASS}
							placeholder={t("workflow.db.addEnumOption")}
							value={enumInput}
							onChange={(e) => setEnumInput(e.target.value)}
						/>
						<Button onClick={handleAddEnum}>{t("workflow.db.add")}</Button>
					</HStack>
					<HStack className="w-full justify-start flex-wrap gap-[6px]">
						{enumOpts.map((opt) => (
							<button
								type="button"
								key={opt}
								onClick={() => setEnumOpts((c) => c.filter((o) => o !== opt))}
								className={CHIP_BUTTON_CLASS}
							>
								{opt}
							</button>
						))}
					</HStack>
				</VStack>
			) : null}
			<Button variant="primary" className="w-full" onClick={handleAdd}>
				{t("workflow.db.addSchemaField")}
			</Button>
			<VStack className="w-full items-stretch justify-start gap-[6px] border border-outline rounded-[8px] p-[8px]">
				{fields.length === 0 ? (
					<span className="text-sm text-ct-regular p-[4px]">{t("workflow.db.noNestedFields")}</span>
				) : (
					fields.map((field) => (
						<HStack
							key={field.key}
							className="w-full items-center bg-bg-regular px-[10px] py-[8px] rounded-[8px]"
						>
							<VStack className="flex-1 items-start">
								<span className="text-xs text-ct-regular">
									{field.type}
									{field.required ? t("workflow.db.requiredSuffix") : ""}
								</span>
								<span className="text-sm text-ct-strong">{field.key}</span>
							</VStack>
							<button
								type="button"
								className="p-[6px] rounded-[6px] bg-bg-regular text-ct-regular"
								onClick={() => onChange(fields.filter((f) => f.key !== field.key))}
							>
								<Icon name="Trash" width={16} height={16} />
							</button>
						</HStack>
					))
				)}
			</VStack>
		</VStack>
	);
};

const stringifyValue = (value: unknown) => {
	if (value === null || value === undefined) {
		return "";
	}
	if (typeof value === "string") {
		return value;
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return String(value);
	}
	try {
		return JSON.stringify(value);
	} catch {
		return "";
	}
};

const getNestedValue = (source: unknown, path: string[]): unknown => {
	let current: unknown = source;
	for (const key of path) {
		if (!current || typeof current !== "object") {
			return undefined;
		}
		current = (current as Record<string, unknown>)[key];
	}
	return current;
};

const setNestedValue = (
	source: WorkflowDbRecord,
	path: string[],
	value: unknown,
): WorkflowDbRecord => {
	if (path.length === 0) {
		return source;
	}

	const [head, ...rest] = path;
	if (rest.length === 0) {
		return { ...source, [head]: value };
	}

	const child = source[head];
	const childObject =
		child && typeof child === "object" && !Array.isArray(child) ? (child as WorkflowDbRecord) : {};

	return {
		...source,
		[head]: setNestedValue(childObject, rest, value),
	};
};

const getTableNames = (schema: WorkflowDbSchema) =>
	schema.properties
		.filter((field) => field.type === "array" && Array.isArray(field.arrayItemProperties))
		.map((field) => field.key);

export const WorkflowDatabaseModal = ({
	isOpen,
	workflowId,
	onClose,
}: WorkflowDatabaseModalProps) => {
	const { t } = useTranslation();

	const schemaQuery = useGetWorkflowDbSchema(workflowId);
	const dataQuery = useGetWorkflowDbData(workflowId);
	const updateSchema = useUpdateWorkflowDbSchema();
	const writeRecord = useWriteRecord();
	const deleteRecord = useDeleteRecord();
	const updateRecord = useUpdateRecord();
	const pendingUpdatesRef = useRef<Map<string, unknown>>(new Map());

	const [draftSchema, setDraftSchema] = useState<WorkflowDbSchema>(createEmptyWorkflowDbSchema());
	const [draftTableData, setDraftTableData] = useState<WorkflowDbTableData>({});
	const [selectedTable, setSelectedTable] = useState("");
	const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

	const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);
	const tableDropdownRef = useRef<HTMLDivElement | null>(null);

	const [isTableModalOpen, setIsTableModalOpen] = useState(false);
	const [tableNameInput, setTableNameInput] = useState("");
	const [tableNameError, setTableNameError] = useState("");

	const [schemaTargetPath, setSchemaTargetPath] = useState("");
	const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
	const [fieldModalMode, setFieldModalMode] = useState<FieldModalMode>("add");
	const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
	const [fieldKey, setFieldKey] = useState("");
	const [fieldType, setFieldType] = useState<WorkflowDbFieldType>("string");
	const [arrayItemType, setArrayItemType] = useState<WorkflowDbArrayItemType>("string");
	const [isRequired, setIsRequired] = useState(false);
	const [enumInput, setEnumInput] = useState("");
	const [enumOptions, setEnumOptions] = useState<string[]>([]);
	const [fieldError, setFieldError] = useState("");
	const [objectPropertiesDraft, setObjectPropertiesDraft] = useState<WorkflowDbFieldSchema[]>([]);

	const [schemaRevision, setSchemaRevision] = useState(0);
	const [dataRevision, setDataRevision] = useState(0);

	const tableNames = useMemo(() => getTableNames(draftSchema), [draftSchema]);

	const initializedRef = useRef(false);
	useEffect(() => {
		if (!isOpen) {
			initializedRef.current = false;
			return;
		}

		if (initializedRef.current || !schemaQuery.data || !dataQuery.data) return;
		initializedRef.current = true;

		const schema = normalizeWorkflowDbSchema(schemaQuery.data.schema, schemaQuery.data.enums);
		const names = getTableNames(schema);
		const data = normalizeWorkflowDbTableData(dataQuery.data.data, names);
		setDraftSchema(schema);
		setDraftTableData(data);
		setSelectedTable(names[0] ?? "");
		setSelectedRowIndex(null);
		setIsTableDropdownOpen(false);
		setIsTableModalOpen(false);
		setSchemaTargetPath("");
		setIsFieldModalOpen(false);
		setFieldError("");
		setTableNameError("");
		setSchemaRevision(0);
		setDataRevision(0);
	}, [isOpen, schemaQuery.data, dataQuery.data]);

	useEffect(() => {
		if (!tableNames.includes(selectedTable)) {
			setSelectedTable(tableNames[0] ?? "");
			setSelectedRowIndex(null);
		}
	}, [selectedTable, tableNames]);

	useEffect(() => {
		if (!isTableDropdownOpen) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			const root = tableDropdownRef.current;
			if (!root) {
				return;
			}
			if (!root.contains(event.target as Node)) {
				setIsTableDropdownOpen(false);
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		return () => document.removeEventListener("pointerdown", handlePointerDown);
	}, [isTableDropdownOpen]);

	const selectedTableFields = useMemo(
		() => (selectedTable ? getFieldsAtPath(draftSchema, `${selectedTable}[]`) : []),
		[draftSchema, selectedTable],
	);

	const selectedTableRows = selectedTable ? (draftTableData[selectedTable] ?? []) : [];
	const selectedRow =
		selectedRowIndex !== null &&
		selectedRowIndex >= 0 &&
		selectedRowIndex < selectedTableRows.length
			? selectedTableRows[selectedRowIndex]
			: null;

	useEffect(() => {
		if (selectedRowIndex === null) {
			return;
		}
		if (selectedRowIndex >= selectedTableRows.length) {
			setSelectedRowIndex(selectedTableRows.length > 0 ? selectedTableRows.length - 1 : null);
		}
	}, [selectedRowIndex, selectedTableRows.length]);

	const selectedPathFields = useMemo(
		() => (schemaTargetPath ? getFieldsAtPath(draftSchema, schemaTargetPath) : []),
		[draftSchema, schemaTargetPath],
	);

	const resetFieldForm = () => {
		setFieldKey("");
		setFieldType("string");
		setArrayItemType("string");
		setIsRequired(false);
		setEnumInput("");
		setEnumOptions([]);
		setFieldError("");
		setObjectPropertiesDraft([]);
	};

	const openAddFieldModal = (path: string) => {
		setSchemaTargetPath(path);
		setFieldModalMode("add");
		setEditingFieldKey(null);
		resetFieldForm();
		setIsFieldModalOpen(true);
	};

	const openEditFieldModal = (path: string, field: WorkflowDbFieldSchema) => {
		setSchemaTargetPath(path);
		setFieldModalMode("edit");
		setEditingFieldKey(field.key);
		setFieldKey(field.key);
		setFieldType(field.type);
		setIsRequired(field.required);
		setEnumOptions(field.enumOptions ?? []);
		setEnumInput("");
		setFieldError("");
		setObjectPropertiesDraft(field.type === "object" ? (field.properties ?? []) : []);
		setArrayItemType(
			field.type === "array" && Array.isArray(field.arrayItemProperties)
				? "string"
				: (field.arrayItemType ?? "string"),
		);
		setIsFieldModalOpen(true);
	};

	const handleSubmitField = () => {
		const key = fieldKey.trim();
		if (!key) {
			setFieldError(t("workflow.db.fieldKeyRequired"));
			return;
		}

		const duplicated =
			fieldModalMode === "edit"
				? selectedPathFields.some((field) => field.key !== editingFieldKey && field.key === key)
				: hasDuplicateFieldKey(selectedPathFields, key);
		if (duplicated) {
			setFieldError(t("workflow.db.fieldKeyExists"));
			return;
		}

		if (fieldType === "enum" && enumOptions.length === 0) {
			setFieldError(t("workflow.db.enumRequired"));
			return;
		}

		const nextField: WorkflowDbFieldSchema = {
			key,
			type: fieldType,
			required: isRequired,
		};

		if (fieldType === "enum") {
			nextField.enumOptions = enumOptions;
		}
		if (fieldType === "array") {
			nextField.arrayItemType = arrayItemType;
			if (arrayItemType === "enum") {
				nextField.enumOptions = enumOptions;
			}
		}
		if (fieldType === "object") {
			nextField.properties = objectPropertiesDraft;
		}

		if (fieldModalMode === "edit" && editingFieldKey) {
			setDraftSchema((current) =>
				updateFieldAtPath(current, schemaTargetPath, editingFieldKey, nextField),
			);
		} else {
			setDraftSchema((current) => addFieldAtPath(current, schemaTargetPath, nextField));
		}

		setSchemaRevision((current) => current + 1);
		setIsFieldModalOpen(false);
		setEditingFieldKey(null);
	};

	const handleDeleteField = (path: string, fieldKeyValue: string) => {
		setDraftSchema((current) => removeFieldAtPath(current, path, fieldKeyValue));
		setSchemaRevision((current) => current + 1);
	};

	const handleAddEnumOption = () => {
		const value = enumInput.trim();
		if (!value) {
			return;
		}
		if (!enumOptions.includes(value)) {
			setEnumOptions((current) => [...current, value]);
		}
		setEnumInput("");
	};

	const handleRemoveEnumOption = (value: string) => {
		setEnumOptions((current) => current.filter((option) => option !== value));
	};

	const handleCreateTable = () => {
		const tableName = tableNameInput.trim();
		if (!tableName) {
			setTableNameError(t("workflow.db.tableNameRequired"));
			return;
		}
		if (draftSchema.properties.some((field) => field.key === tableName)) {
			setTableNameError(t("workflow.db.tableNameExists"));
			return;
		}

		const tableField: WorkflowDbFieldSchema = {
			key: tableName,
			type: "array",
			required: false,
			arrayItemProperties: [],
		};

		setDraftSchema((current) => addFieldAtPath(current, "$", tableField));
		setDraftTableData((current) => ({ ...current, [tableName]: [] }));
		setSelectedTable(tableName);
		setSelectedRowIndex(null);
		setSchemaRevision((current) => current + 1);
		setIsTableModalOpen(false);
	};

	const handleAddRow = () => {
		if (!selectedTable) {
			return;
		}
		const rowSchema: WorkflowDbSchema = { type: "object", properties: selectedTableFields };
		const newRow = createEmptyRecordFromSchema(rowSchema);
		writeRecord.mutate({ workflowId, table: selectedTable, data: newRow });
	};

	const handleDeleteRow = (rowIndex: number) => {
		if (!selectedTable) {
			return;
		}
		const rows = draftTableData[selectedTable] ?? [];
		const row = rows[rowIndex];
		const rowId = row?._id as string | undefined;
		if (rowId) {
			deleteRecord.mutate({
				workflowId,
				table: selectedTable,
				ref: `${selectedTable}._id["${rowId}"]`,
			});
		}
		setDraftTableData((current) => {
			const currentRows = current[selectedTable] ?? [];
			const nextRows = currentRows.filter((_, index) => index !== rowIndex);
			return { ...current, [selectedTable]: nextRows };
		});
		if (selectedRowIndex === rowIndex) {
			setSelectedRowIndex(null);
		} else if (selectedRowIndex !== null && selectedRowIndex > rowIndex) {
			setSelectedRowIndex(selectedRowIndex - 1);
		}
	};

	const handleDeleteTable = (tableName: string) => {
		setDraftSchema((current) => removeFieldAtPath(current, "$", tableName));
		setDraftTableData((current) => {
			const next = { ...current };
			delete next[tableName];
			return next;
		});
		if (selectedTable === tableName) {
			setSelectedTable("");
			setSelectedRowIndex(null);
		}
		setSchemaRevision((current) => current + 1);
	};

	const updateSelectedRowByPath = (path: string[], value: unknown) => {
		if (!selectedTable || selectedRowIndex === null) {
			return;
		}
		const rows = draftTableData[selectedTable] ?? [];
		const row = rows[selectedRowIndex];
		const rowId = row?._id as string | undefined;
		setDraftTableData((current) => {
			const currentRows = current[selectedTable] ?? [];
			const nextRows = currentRows.map((r, index) =>
				index === selectedRowIndex ? setNestedValue(r, path, value) : r,
			);
			return { ...current, [selectedTable]: nextRows };
		});
		if (rowId) {
			const fullPath = `${selectedTable}._id["${rowId}"].${path.join(".")}`;
			pendingUpdatesRef.current.set(fullPath, value);
			setDataRevision((current) => current + 1);
		}
	};

	const renderArrayEditor = (field: WorkflowDbFieldSchema, path: string[]) => {
		const value = selectedRow ? getNestedValue(selectedRow, path) : undefined;
		const arrayItems = Array.isArray(value) ? value : [];
		const itemType = field.arrayItemType ?? "string";

		const setItem = (index: number, nextValue: unknown) => {
			const next = [...arrayItems];
			next[index] = nextValue;
			updateSelectedRowByPath(path, next);
		};

		const removeItem = (index: number) => {
			updateSelectedRowByPath(
				path,
				arrayItems.filter((_, itemIndex) => itemIndex !== index),
			);
		};

		const addItem = () => {
			const defaultValue =
				itemType === "number"
					? 0
					: itemType === "boolean"
						? false
						: itemType === "enum"
							? (field.enumOptions?.[0] ?? "")
							: "";
			updateSelectedRowByPath(path, [...arrayItems, defaultValue]);
		};

		return (
			<VStack
				className={`w-full items-stretch justify-start gap-[8px] ${INSET_SURFACE_CLASS} p-[10px]`}
			>
				<HStack className="w-full justify-between px-[2px]">
					<span className="text-xs font-medium uppercase tracking-[0.02em] text-ct-regular">
						{t("workflow.db.arrayItems")}
					</span>
					<span className="text-xs text-ct-weak">{arrayItems.length}</span>
				</HStack>
				{arrayItems.length === 0 ? (
					<span className="text-xs text-ct-regular">{t("workflow.db.noItems")}</span>
				) : null}
				{arrayItems.map((item, index) => (
					<HStack
						key={`${path.join(".")}-${index}`}
						className={`w-full items-start gap-[8px] ${SECTION_SURFACE_CLASS} p-[8px]`}
					>
						<div className="w-full">
							{itemType === "number" ? (
								<input
									type="number"
									className={INPUT_SURFACE_CLASS}
									value={typeof item === "number" ? item : 0}
									onChange={(event) => {
										const parsed = Number(event.target.value);
										if (Number.isFinite(parsed)) {
											setItem(index, parsed);
										}
									}}
								/>
							) : itemType === "boolean" ? (
								<VStack
									className={`w-full items-stretch justify-start gap-[6px] ${INSET_SURFACE_CLASS} p-[10px]`}
								>
									<BinaryChoiceControl
										label={t("workflow.db.itemLabel", { number: index + 1 })}
										value={Boolean(item)}
										onChange={(nextValue) => setItem(index, nextValue)}
										trueLabel="true"
										falseLabel="false"
										className="w-full"
									/>
								</VStack>
							) : itemType === "enum" && field.enumOptions ? (
								<SelectInputField
									label={t("workflow.db.itemLabel", { number: index + 1 })}
									value={typeof item === "string" ? item : field.enumOptions[0]}
									onChange={(nextValue: string) => setItem(index, nextValue)}
									options={field.enumOptions.map((option) => ({
										label: option,
										value: option,
									}))}
								/>
							) : (
								<input
									className={INPUT_SURFACE_CLASS}
									value={typeof item === "string" ? item : ""}
									onChange={(event) => setItem(index, event.target.value)}
								/>
							)}
						</div>
						<Button className="h-[46px] shrink-0" onClick={() => removeItem(index)}>
							{t("workflow.db.remove")}
						</Button>
					</HStack>
				))}
				<Button className="w-full" onClick={addItem}>
					<HStack className="w-full justify-center gap-[8px]">
						<Icon name="Add" width={14} height={14} />
						<span>{t("workflow.db.addItem")}</span>
					</HStack>
				</Button>
			</VStack>
		);
	};

	const renderFieldEditor = (field: WorkflowDbFieldSchema, rowPath: string[]) => {
		const value = selectedRow ? getNestedValue(selectedRow, rowPath) : undefined;
		const key = rowPath.join(".");

		if (field.type === "object") {
			const nestedFields = field.properties ?? [];
			return (
				<VStack
					key={key}
					className="w-full items-stretch justify-start gap-[8px]"
				>
					<HStack className="w-full justify-between text-sm text-ct-strong font-medium px-[6px]">
						{field.key}
					</HStack>
					{nestedFields.length === 0 ? (
						<span className="text-xs text-ct-regular">{t("workflow.db.noNestedFieldsShort")}</span>
					) : (
						<VStack className="w-full outline-outline rounded-[10px] p-[6px]">
							{nestedFields.map((child) => renderFieldEditor(child, [...rowPath, child.key]))}
						</VStack>
					)}
				</VStack>
			);
		}

		if (field.type === "boolean") {
			return (
				<BinaryChoiceControl
					key={key}
					label={field.key}
					value={Boolean(value)}
					onChange={(nextValue) => updateSelectedRowByPath(rowPath, nextValue)}
					trueLabel="true"
					falseLabel="false"
					className="w-full"
				/>
			);
		}

		if (field.type === "enum" && field.enumOptions) {
			return (
				<VStack key={key} className="w-full items-start gap-[4px]">
					<SelectInputField
						label={field.key}
						value={typeof value === "string" ? value : field.enumOptions[0]}
						onChange={(nextValue: string) => updateSelectedRowByPath(rowPath, nextValue)}
						options={field.enumOptions.map((option) => ({ label: option, value: option }))}
					/>
				</VStack>
			);
		}

		if (field.type === "number") {
			return (
				<TextInputField
					key={key}
					label={field.key}
					type="number"
					value={typeof value === "number" ? value : 0}
					onChange={(event) => {
						const parsed = Number(event.target.value);
						if (Number.isFinite(parsed)) {
							updateSelectedRowByPath(rowPath, parsed);
						}
					}}
				/>
			);
		}

		if (field.type === "array") {
			return (
				<VStack key={key} className="w-full items-start gap-[4px]">
					<span className="text-sm text-ct-strong">{field.key}</span>
					{renderArrayEditor(field, rowPath)}
				</VStack>
			);
		}

		if (field.type === "file") {
			const fileName = typeof value === "string" ? value : "";
			return (
				<VStack key={key} className="w-full items-start justify-start gap-[6px] p-[2px]">
					<span className="text-sm text-ct-strong px-[6px]">{field.key}</span>
					<button
						type="button"
						className={`${INPUT_SURFACE_CLASS} text-left cursor-pointer`}
						onClick={() => {
							const input = document.createElement("input");
							input.type = "file";
							input.onchange = () => {
								const file = input.files?.[0];
								if (file) {
									updateSelectedRowByPath(rowPath, file.name);
								}
							};
							input.click();
						}}
					>
						<span className={fileName ? "text-ct-strong" : "text-ct-regular"}>
							{fileName || t("workflow.db.selectFile")}
						</span>
					</button>
				</VStack>
			);
		}

		return (
			<TextInputField
				key={key}
				label={field.key}
				value={typeof value === "string" ? value : ""}
				onChange={(event) => updateSelectedRowByPath(rowPath, event.target.value)}
			/>
		);
	};

	useEffect(() => {
		if (!isOpen || schemaRevision === 0) {
			return;
		}
		const timer = window.setTimeout(() => {
			updateSchema.mutate({ workflowId, request: { schema: draftSchema } });
		}, 500);
		return () => window.clearTimeout(timer);
	}, [draftSchema, isOpen, schemaRevision, updateSchema, workflowId]);

	useEffect(() => {
		if (!isOpen || dataRevision === 0) {
			return;
		}
		const timer = window.setTimeout(() => {
			const sets = Array.from(pendingUpdatesRef.current.entries()).map(
				([path, value]) => ({ path, value }),
			);
			if (sets.length > 0) {
				updateRecord.mutate({ workflowId, request: { sets } });
				pendingUpdatesRef.current.clear();
			}
		}, 500);
		return () => window.clearTimeout(timer);
	}, [dataRevision, isOpen, updateRecord, workflowId]);

	const isLoading = schemaQuery.isLoading || dataQuery.isLoading;

	return (
		<Modal isOpen={isOpen} onClose={onClose} className="w-[min(96vw,1280px)] h-[min(88vh,900px)]">
			<VStack className="w-full h-full min-h-0 overflow-hidden rounded-[24px] bg-bg-weak/95 border-outline p-[16px] gap-[12px] items-stretch justify-start backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
				<HStack className="w-full justify-between items-center p-[4px] shrink-0">
					<VStack className="items-start gap-[2px]">
						<span className="text-xl font-semibold text-ct-strong">{t("workflow.db.title")}</span>
						<span className="text-sm text-ct-regular">{t("workflow.db.description")}</span>
					</VStack>
					<button type="button" onClick={onClose} className="p-[6px] rounded-[10px] text-ct-regular shrink-0">
						<Icon name="Close" width={20} height={20} />
					</button>
				</HStack>

				{isLoading ? (
					<VStack className="w-full h-full min-h-0 justify-center items-center">
						<Spinner size="lg" />
					</VStack>
				) : (
					<HStack className="w-full flex-1 min-h-0 min-w-0 gap-[12px] overflow-hidden items-stretch justify-start">
						<VStack
							className={`w-[280px] h-full min-h-0 items-stretch justify-start p-[12px] gap-[10px] shrink-0 ${PANEL_SURFACE_CLASS}`}
						>
							<div ref={tableDropdownRef} className="w-full relative shrink-0">
								<button
									type="button"
									onClick={() => setIsTableDropdownOpen((current) => !current)}
									className="w-full"
								>
									<HStack className="w-full justify-between text-ct-strong text-lg font-medium p-[4px]">
										<span>{selectedTable || t("workflow.db.selectTable")}</span>
										<Icon name="Down" width={24} height={24} />
									</HStack>
								</button>
								{isTableDropdownOpen ? (
									<VStack className="w-full absolute left-0 top-full mt-[8px] rounded-[14px] bg-bg-weak/95 border border-outline p-[8px] z-30 gap-[6px] items-stretch justify-start backdrop-blur-md shadow-[0_12px_40px_rgba(15,23,42,0.1)]">
										<VStack className="w-full gap-[4px] items-stretch justify-start">
											{tableNames.map((name) => (
												<HStack
													key={name}
													className="w-full justify-between items-center p-[8px] text-sm font-medium text-ct-strong"
												>
													<button
														type="button"
														className="flex-1 text-left"
														onClick={() => {
															setSelectedTable(name);
															setSelectedRowIndex(null);
															setIsTableDropdownOpen(false);
														}}
													>
														<span className="w-full">{name}</span>
													</button>
													<button
														type="button"
														className="p-[4px] rounded-[6px] text-ct-regular shrink-0"
														onClick={(event) => {
															event.stopPropagation();
															handleDeleteTable(name);
															setIsTableDropdownOpen(false);
														}}
													>
														<Icon name="Trash" width={14} height={14} />
													</button>
												</HStack>
											))}
										</VStack>
										<Button
											className="w-full"
											onClick={() => {
												setIsTableModalOpen(true);
												setIsTableDropdownOpen(false);
											}}
										>
											<HStack className="w-full justify-center gap-[8px]">
												<Icon name="Add" width={14} height={14} />
												<span>{t("workflow.db.addTable")}</span>
											</HStack>
										</Button>
									</VStack>
								) : null}
							</div>

							<VStack className="w-full flex-1 min-h-0 overflow-auto items-stretch justify-start gap-[8px]">
								{selectedTableFields.map((field) => (
									<button
										type="button"
										key={field.key}
										className="w-full"
										onClick={() => openEditFieldModal(`${selectedTable}[]`, field)}
									>
										<HStack className="w-full bg-bg-regular/60 px-[12px] py-[10px] rounded-[10px] justify-start items-center hover:bg-bg-regular transition-colors">
											<VStack className="flex-1 items-start">
												<span className="text-xs text-ct-regular">
													{field.type}
													{field.required ? t("workflow.db.requiredSuffix") : ""}
												</span>
												<span className="text-sm font-medium text-ct-strong">{field.key}</span>
											</VStack>
											<button
												type="button"
												className="p-[6px] rounded-[6px] text-ct-regular shrink-0"
												onClick={(event) => {
													event.stopPropagation();
													handleDeleteField(`${selectedTable}[]`, field.key);
												}}
											>
												<Icon name="Trash" width={18} height={18} />
											</button>
										</HStack>
									</button>
								))}
							</VStack>

							<Button
								className="w-full mt-[8px] shrink-0"
								onClick={() => selectedTable && openAddFieldModal(`${selectedTable}[]`)}
								disabled={!selectedTable}
							>
								<HStack className="w-full justify-center gap-[8px]">
									<Icon name="Add" width={14} height={14} />
									<span>{t("workflow.db.addField")}</span>
								</HStack>
							</Button>
						</VStack>

						<HStack className="w-full h-full flex-1 min-w-0 min-h-0 items-stretch justify-start gap-[12px] overflow-auto">
							<VStack
								className={`h-full min-w-[480px] flex-1 items-stretch justify-start p-[12px] gap-[10px] ${PANEL_SURFACE_CLASS}`}
							>
								<div className="w-full flex-1 min-h-0 min-w-0 overflow-auto rounded-[12px] border border-outline bg-bg-regular/30">
									<table className="w-full min-w-[720px] text-sm">
										<thead>
											<tr className="border-b border-outline">
												<th className="text-left px-[10px] py-[8px] text-ct-strong">#</th>
												{selectedTableFields.map((field) => (
													<th
														key={field.key}
														className="text-left px-[10px] py-[8px] text-ct-strong"
													>
														{field.key}
													</th>
												))}
												<th className="w-[40px]" />
											</tr>
										</thead>
										<tbody>
											{selectedTableRows.length === 0 ? (
												<tr>
													<td
														className="px-[10px] py-[16px] text-ct-regular text-center"
														colSpan={Math.max(selectedTableFields.length + 2, 2)}
													>
														{t("workflow.db.noRows")}
													</td>
												</tr>
											) : (
												selectedTableRows.map((row, rowIndex) => (
													<tr
														key={`${selectedTable}-${rowIndex}`}
														className={`border-t border-outline cursor-pointer transition-colors ${selectedRowIndex === rowIndex ? "bg-pm-weak/10" : "hover:bg-hover"}`}
														onClick={() => setSelectedRowIndex(rowIndex)}
													>
														<td className="px-[10px] py-[8px] text-ct-regular">{rowIndex + 1}</td>
														{selectedTableFields.map((field) => (
															<td key={field.key} className="px-[10px] py-[8px] text-ct-strong">
																<span className="line-clamp-1">
																	{stringifyValue(row[field.key])}
																</span>
															</td>
														))}
														<td className="px-[4px] py-[8px]">
															<button
																type="button"
																className="p-[4px] rounded-[6px] text-ct-regular"
																onClick={(event) => {
																	event.stopPropagation();
																	handleDeleteRow(rowIndex);
																}}
															>
																<Icon name="Trash" width={14} height={14} />
															</button>
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>

								<Button
									className="w-full mt-[8px] shrink-0"
									onClick={handleAddRow}
									disabled={!selectedTable}
								>
									<HStack className="w-full justify-center gap-[8px]">
										<Icon name="Add" width={14} height={14} />
										<span>{t("workflow.db.addRow")}</span>
									</HStack>
								</Button>
							</VStack>

							{selectedRow ? (
								<VStack
									className={`w-full max-w-[360px] h-full min-h-0 items-stretch justify-start p-[12px] gap-[10px] overflow-auto shrink-0 ${PANEL_SURFACE_CLASS}`}
								>
									<HStack className="w-full justify-between shrink-0">
										<span className="text-lg font-semibold text-ct-strong">
											{t("workflow.db.rowLabel", { number: (selectedRowIndex ?? 0) + 1 })}
										</span>
										<HStack className="gap-[6px]">
											<button
												type="button"
												className="p-[6px] rounded-[8px] text-ct-regular"
												onClick={() => {
													if (selectedRowIndex !== null) {
														handleDeleteRow(selectedRowIndex);
													}
												}}
											>
												<Icon name="Trash" width={20} height={20} />
											</button>
											<button
												type="button"
												className="p-[6px] rounded-[8px] text-ct-regular"
												onClick={() => {
													setSelectedRowIndex(null)
												}}
											>
												<Icon name="Close" width={20} height={20} />
											</button>
										</HStack>
									</HStack>
									<VStack className="w-full items-stretch justify-start gap-[10px] min-w-0">
										{selectedTableFields.map((field) => renderFieldEditor(field, [field.key]))}
									</VStack>
								</VStack>
							) : null}
						</HStack>
					</HStack>
				)}

				<Modal
					isOpen={isTableModalOpen}
					onClose={() => setIsTableModalOpen(false)}
					className="w-full max-w-[420px]"
				>
					<VStack className="w-full rounded-[24px] bg-bg-weak/95 border border-outline p-[20px] gap-[10px] items-stretch justify-start backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
						<span className="text-lg font-semibold text-ct-strong p-[4px]">{t("workflow.db.addTable")}</span>
						<TextInputField
							label={t("workflow.db.tableName")}
							value={tableNameInput}
							onChange={(event) => {
								setTableNameError("");
								setTableNameInput(event.target.value);
							}}
							placeholder="ex) customers"
							clearable
							errorMessage={tableNameError || undefined}
						/>
						<HStack className="w-full gap-[8px] mt-[10px] pt-[4px]">
							<Button className="w-full" onClick={() => setIsTableModalOpen(false)}>
								{t("workflow.db.cancel")}
							</Button>
							<Button variant="primary" className="w-full" onClick={handleCreateTable}>
								{t("workflow.db.create")}
							</Button>
						</HStack>
					</VStack>
				</Modal>

				<Modal
					isOpen={isFieldModalOpen}
					onClose={() => setIsFieldModalOpen(false)}
					className="w-full max-w-[520px]"
				>
					<VStack className="w-full rounded-[24px] bg-bg-weak/95 border border-outline p-[20px] gap-[10px] items-stretch justify-start max-h-[82vh] backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
						<span className="text-md font-semibold text-ct-strong shrink-0">
							{fieldModalMode === "edit" ? t("workflow.db.editField") : t("workflow.db.addField")}
						</span>
						<VStack className="w-full items-stretch justify-start gap-[10px] overflow-auto min-h-0">
						<TextInputField
							label={t("workflow.db.fieldKey")}
							value={fieldKey}
							onChange={(event) => {
								setFieldError("");
								setFieldKey(event.target.value);
							}}
							placeholder="ex) status"
							clearable
							errorMessage={fieldError || undefined}
						/>
						<HStack className="w-full items-end gap-[8px]">
							<div className="flex-1 min-w-0">
								<SelectInputField
									label={t("workflow.db.type")}
									value={fieldType}
									onChange={(value: string) => setFieldType(value as WorkflowDbFieldType)}
									options={FIELD_TYPE_OPTIONS}
								/>
							</div>
							<BinaryChoiceControl label={t("workflow.db.required")} value={isRequired} onChange={setIsRequired} />
						</HStack>
						{fieldType === "array" ? (
							<SelectInputField
								label={t("workflow.db.arrayItemType")}
								value={arrayItemType}
								onChange={(value: string) => setArrayItemType(value as WorkflowDbArrayItemType)}
								options={ARRAY_ITEM_TYPE_OPTIONS}
							/>
						) : null}
						{fieldType === "enum" || (fieldType === "array" && arrayItemType === "enum") ? (
							<VStack className="w-full items-stretch justify-start gap-[6px] border border-outline rounded-[10px] p-[10px]">
								<HStack className="w-full gap-[6px]">
									<input
										className={INPUT_SURFACE_CLASS}
										placeholder={t("workflow.db.addEnumOption")}
										value={enumInput}
										onChange={(event) => setEnumInput(event.target.value)}
									/>
									<Button onClick={handleAddEnumOption}>{t("workflow.db.add")}</Button>
								</HStack>
								<HStack className="w-full justify-start flex-wrap gap-[6px]">
									{enumOptions.map((option) => (
										<button
											type="button"
											key={option}
											onClick={() => handleRemoveEnumOption(option)}
											className={CHIP_BUTTON_CLASS}
										>
											{option}
										</button>
									))}
								</HStack>
							</VStack>
						) : null}
						{fieldType === "object" ? (
							<ObjectSchemaEditor
								fields={objectPropertiesDraft}
								onChange={setObjectPropertiesDraft}
							/>
						) : null}
						</VStack>
						<HStack className="w-full gap-[8px] pt-[4px] shrink-0">
							<Button className="w-full" onClick={() => setIsFieldModalOpen(false)}>
								{t("workflow.db.cancel")}
							</Button>
							<Button variant="primary" className="w-full" onClick={handleSubmitField}>
								{fieldModalMode === "edit" ? t("workflow.db.updateField") : t("workflow.db.addField")}
							</Button>
						</HStack>
					</VStack>
				</Modal>
			</VStack>
		</Modal>
	);
};
